import { concat, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismCard, PrismDataBar } from '@mylighthouse/prism-ember';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import { and, eq, gt, not } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import GeneralButtonsButtonExpandArrow from 'lumen/components/general/buttons/button-expand-arrow';
import scrollIntoView from 'lumen/modifiers/scroll-into-view';

import styles from './short-term-rental-breakdown-block.module.css';

interface HotelShortTermRentalShortTermRentalBreakdownBlockSignature {
  Args: {
    activeFilters: unknown;
    addFilter: unknown;
    blockTitle: unknown;
    isLoading: unknown;
    items: unknown;
  };
}

export default class HotelShortTermRentalShortTermRentalBreakdownBlock extends Component<HotelShortTermRentalShortTermRentalBreakdownBlockSignature> {
  @tracked expanded = false;

  EXPANDABLE_ITEM = 4;

  SCROLL_TO_ITEM = 2;

  @action
  toggleBreakdown(): void {
    this.expanded = !this.expanded;
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable require-presentational-children }}
    <div class={{styles.card}}>
      {{#if @isLoading}}
        <PrismCard @contentLayout="noPadding" class={{styles.card-item}}>
          <:header>
            <div class="{{styles.loading-header}} mb-500 flex items-center">
              <span class="loading-bar"></span>
            </div>
          </:header>

          <:content>
            <div class={{styles.loading-content}}>
              <IncrementFor @times={{5}}>
                <span class={{styles.loading-content}}>
                  <span class="loading-bar"></span>
                </span>
              </IncrementFor>
            </div>
          </:content>
        </PrismCard>
      {{else}}
        <PrismCard @contentLayout="noPadding" class={{styles.card-item}}>
          <:header as |Header|>
            <Header.title>
              {{@blockTitle}}
            </Header.title>
          </:header>

          <:content>
            <div class="text-200--regular">
              {{#each @items as |item index|}}
                {{#if (and (eq index 4) (gt @items.length 5))}}
                  <div
                    class="{{styles.item}} button--unstyled"
                    role="button"
                    {{on "click" this.toggleBreakdown}}
                  >
                    <IncrementFor @times={{1}}>
                      <div
                        class={{concat
                          styles.indent-container
                          " "
                          "flex items-center justify-center"
                        }}
                      >
                        <GeneralButtonsButtonExpandArrow
                          @isExpanded={{this.expanded}}
                        />
                      </div>
                    </IncrementFor>
                    {{#if item.progressBarData}}
                      <PrismDataBar @data={{item.progressBarData}} as |DataBar|>
                        <DataBar.label
                          @text={{item.name}}
                          @tooltip={{hash message=item.tooltipMessage}}
                        />
                        <DataBar.value @text={{item.value}} />
                      </PrismDataBar>
                    {{else}}
                      <span>{{item.name}}</span>
                    {{/if}}
                  </div>
                {{else}}
                  <button
                    class="{{styles.item}}
                      button--unstyled
                      {{if (and (not this.expanded) (gt index 4)) 'hidden'}}
                      {{if
                        (includes item.key @activeFilters)
                        styles.item--selected
                      }}
                      "
                    disabled={{item.isDisabled}}
                    type="button"
                    {{on "click" (fn @addFilter item)}}
                  >
                    <IncrementFor @times={{if (gt index 4) 2 1}}>
                      <div class={{styles.indent-container}}>
                        {{#if (eq index 2)}}
                          <div
                            {{scrollIntoView shouldScroll=this.expanded}}
                          ></div>
                        {{/if}}
                      </div>
                    </IncrementFor>
                    {{#if item.progressBarData}}
                      <PrismDataBar @data={{item.progressBarData}} as |DataBar|>
                        <DataBar.label
                          @text={{item.name}}
                          @tooltip={{hash
                            iconSize="extra-small"
                            message=item.tooltipMessage
                          }}
                        />
                        <DataBar.value @text={{item.value}} />
                      </PrismDataBar>
                    {{else}}
                      <span>{{item.name}}</span>
                    {{/if}}
                  </button>
                {{/if}}
              {{/each}}
            </div>
          </:content>
        </PrismCard>
      {{/if}}
    </div>
  </template>
}
